import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";
import AdminNavbar from "../Layout/AdminNavbar";
import API_BASE_URL from "../config";

const T = {
  primary: "#6366f1",
  primaryLight: "#eef2ff",
  primaryDark: "#4f46e5",
  surface: "#ffffff",
  surfaceAlt: "#f8fafc",
  border: "#e2e8f0",
  borderLight: "#f1f5f9",
  text: "#0f172a",
  textMuted: "#64748b",
  textLight: "#94a3b8",
  success: "#10b981",
  successBg: "#ecfdf5",
  danger: "#ef4444",
  dangerBg: "#fef2f2",
  warning: "#f59e0b",
  warningBg: "#fffbeb",
  radius: "10px",
  radiusSm: "6px",
  shadow: "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
  shadowMd: "0 4px 12px rgba(99,102,241,0.10)",
};

const STATUS_MAP = {
  Active:    { bg: "#ecfdf5", color: "#10b981", dot: "#10b981" },
  Planning:  { bg: T.primaryLight, color: T.primary, dot: T.primary },
  "On Hold": { bg: "#fffbeb", color: "#f59e0b", dot: "#f59e0b" },
  Completed: { bg: "#ecfeff", color: "#0891b2", dot: "#0891b2" },
  Archived:  { bg: "#f8fafc", color: "#64748b", dot: "#94a3b8" },
  Cancelled: { bg: "#fef2f2", color: "#ef4444", dot: "#ef4444" },
};

const PRIORITY_MAP = {
  Low:      { bg: "#ecfdf5", color: "#10b981" },
  Medium:   { bg: "#fffbeb", color: "#d97706" },
  High:     { bg: "#fef2f2", color: "#ef4444" },
  Critical: { bg: "#1e293b", color: "#f1f5f9" },
};

const StatusBadge = ({ status }) => {
  if (!status) return <span style={{ color: T.textLight }}>—</span>;
  const s = STATUS_MAP[status] || { bg: "#f1f5f9", color: T.textMuted, dot: T.textMuted };
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: s.bg, color: s.color, whiteSpace: "nowrap" }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: s.dot, flexShrink: 0 }} />
      {status}
    </span>
  );
};

const PriorityBadge = ({ priority }) => {
  if (!priority) return <span style={{ color: T.textLight }}>—</span>;
  const p = PRIORITY_MAP[priority] || { bg: "#f1f5f9", color: T.textMuted };
  return (
    <span style={{ display: "inline-flex", alignItems: "center", padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: p.bg, color: p.color, whiteSpace: "nowrap" }}>
      {priority}
    </span>
  );
};

const ProjectAvatar = ({ project }) => {
  const label = project.icon || (project.name || "?").slice(0, 2).toUpperCase();
  const bg = project.accentColor || T.primary;
  return (
    <div style={{ width: 32, height: 32, borderRadius: 7, background: bg, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0, letterSpacing: 0.3 }}>
      {label}
    </div>
  );
};

const Th = ({ label, sortKey, sortConfig, onSort }) => (
  <th
    onClick={() => sortKey && onSort(sortKey)}
    style={{
      position: "sticky", top: 0, background: "#f1f5f9", color: T.textMuted,
      fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.6,
      padding: "10px 12px", whiteSpace: "nowrap",
      cursor: sortKey ? "pointer" : "default",
      userSelect: "none", borderBottom: `2px solid ${T.border}`, zIndex: 10,
    }}
  >
    {label}
    {sortKey && (
      <span style={{ marginLeft: 4, opacity: sortConfig.key === sortKey ? 1 : 0.3 }}>
        {sortConfig.key === sortKey ? (sortConfig.direction === "asc" ? " ↑" : " ↓") : " ↕"}
      </span>
    )}
  </th>
);

const TdPlain = ({ children, style }) => (
  <td style={{ padding: "10px 12px", fontSize: 13, color: T.text, whiteSpace: "nowrap", ...style }}>
    {children}
  </td>
);

const fmt = (d) => d ? new Date(d).toLocaleDateString("en-GB") : <span style={{ color: T.textLight }}>—</span>;
const val = (v) => v || <span style={{ color: T.textLight }}>—</span>;

// ── Confirmation Modal ─────────────────────────────────────────────────────
const ConfirmModal = ({ isOpen, onClose, onConfirm, title, message, confirmLabel, confirmVariant, icon }) => {
  useEffect(() => {
    if (!isOpen) return;
    const fn = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [isOpen, onClose]);

  if (!isOpen) return null;
  const isWarn = confirmVariant === "warning";

  return (
    <>
      <style>{`
        @keyframes proj-fade { from { opacity:0 } to { opacity:1 } }
        @keyframes proj-slide { from { opacity:0; transform:translate(-50%,-46%) } to { opacity:1; transform:translate(-50%,-50%) } }
      `}</style>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1050, backdropFilter: "blur(2px)", animation: "proj-fade 0.15s ease" }} />
      <div
        role="dialog" aria-modal="true"
        style={{
          position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)",
          zIndex: 1060, width: "min(440px, 90vw)", background: T.surface,
          borderRadius: 12, boxShadow: "0 20px 60px rgba(0,0,0,0.2)", overflow: "hidden",
          animation: "proj-slide 0.2s ease",
        }}
      >
        <div style={{ height: 4, background: isWarn ? `linear-gradient(90deg,${T.warning},#fbbf24)` : `linear-gradient(90deg,${T.danger},#f87171)` }} />
        <div style={{ padding: "24px 24px 20px" }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 14 }}>
            <div style={{ width: 44, height: 44, borderRadius: 10, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: isWarn ? T.warningBg : T.dangerBg, fontSize: 22 }}>
              {icon}
            </div>
            <div>
              <h5 style={{ margin: 0, fontWeight: 700, fontSize: 16, color: T.text }}>{title}</h5>
              <p style={{ margin: "6px 0 0", fontSize: 13, color: T.textMuted, lineHeight: 1.6 }}>{message}</p>
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>
            <button
              onClick={onConfirm}
              style={{ padding: "7px 20px", borderRadius: T.radiusSm, border: "none", background: isWarn ? T.warning : T.danger, color: "#fff", fontWeight: 600, fontSize: 13, cursor: "pointer" }}
            >
              {confirmLabel}
            </button>
            <button
              onClick={onClose}
              style={{ padding: "7px 20px", borderRadius: T.radiusSm, border: `1.5px solid ${T.border}`, background: T.surface, color: T.textMuted, fontWeight: 600, fontSize: 13, cursor: "pointer" }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────
const STATUSES      = ["Planning", "Active", "On Hold", "Completed", "Archived", "Cancelled"];
const PRIORITIES    = ["Low", "Medium", "High", "Critical"];
const PROJECT_TYPES = ["Internal", "Client", "Research", "Maintenance", "Compliance", "Other"];
const PAGE_SIZE     = 10;

const AllProjects = () => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");

  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [downloading, setDownloading] = useState(false);
  const [hoveredRow, setHoveredRow] = useState(null);

  const [modal, setModal] = useState({ isOpen: false, type: null, project: null });
  const openDeleteModal  = (p) => setModal({ isOpen: true, type: "delete",  project: p });
  const openArchiveModal = (p) => setModal({ isOpen: true, type: "archive", project: p });
  const closeModal       = ()  => setModal({ isOpen: false, type: null, project: null });

  // Fixed stats — fetched once on mount, never change with filters
  const [headerStats, setHeaderStats] = useState({ total: 0, active: 0, completed: 0, onHold: 0 });
  const [totalElements, setTotalElements] = useState(0);

  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("token");
    const roles = JSON.parse(localStorage.getItem("roles") || "[]");
    if (!token) { toast.error("You must be logged in."); navigate("/"); return; }
    if (!roles.includes("ROLE_ADMIN")) { toast.error("Not authorized."); navigate("/"); return; }
  }, [navigate]);

  // One-time mount fetch for header stats — unaffected by any filter
  useEffect(() => {
    const token = localStorage.getItem("token");
    fetch(`${API_BASE_URL}/api/projects?page=0&size=1000&sortField=createdAt&sortDir=asc`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((res) => res.ok ? res.json() : null)
      .then((data) => {
        if (!data) return;
        const all = data.content || [];
        setHeaderStats({
          total: data.totalElements ?? all.length,
          active: all.filter((p) => p.status === "Active").length,
          completed: all.filter((p) => p.status === "Completed").length,
          onHold: all.filter((p) => p.status === "On Hold").length,
        });
      })
      .catch(() => {});
  }, []); // empty deps — runs once only

  useEffect(() => {
    const token = localStorage.getItem("token");
    const params = new URLSearchParams({
      page: currentPage, size: PAGE_SIZE,
      sortField: sortConfig.key || "createdAt",
      sortDir: sortConfig.direction,
      ...(search && { search }),
      ...(statusFilter && { status: statusFilter }),
      ...(typeFilter && { projectType: typeFilter }),
      ...(priorityFilter && { priority: priorityFilter }),
    });
    setLoading(true);
    fetch(`${API_BASE_URL}/api/projects?${params}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((res) => { if (!res.ok) throw new Error("Failed to fetch projects"); return res.json(); })
      .then((data) => {
        setProjects(data.content || []);
        setFilteredProjects(data.content || []);
        setTotalPages(data.totalPages || 0);
        setTotalElements(data.totalElements ?? 0);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [currentPage, sortConfig, search, statusFilter, typeFilter, priorityFilter]);

  useEffect(() => {
    let result = [...projects];
    if (search) result = result.filter((p) => p.name?.toLowerCase().includes(search.toLowerCase()) || p.key?.toLowerCase().includes(search.toLowerCase()) || p.referenceNumber?.toLowerCase().includes(search.toLowerCase()));
    if (statusFilter)   result = result.filter((p) => p.status === statusFilter);
    if (typeFilter)     result = result.filter((p) => p.projectType === typeFilter);
    if (priorityFilter) result = result.filter((p) => p.priority === priorityFilter);
    if (sortConfig.key) {
      result.sort((a, b) => {
        const vA = a[sortConfig.key] ?? ""; const vB = b[sortConfig.key] ?? "";
        if (vA < vB) return sortConfig.direction === "asc" ? -1 : 1;
        if (vA > vB) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    setFilteredProjects(result);
  }, [search, statusFilter, typeFilter, priorityFilter, projects, sortConfig]);

  const handleSort = (key) =>
    setSortConfig((prev) => ({ key, direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc" }));

  const handleModalConfirm = () => {
    if (modal.type === "delete")  confirmDelete(modal.project);
    if (modal.type === "archive") confirmArchive(modal.project);
    closeModal();
  };

  const confirmDelete = async (project) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE_URL}/api/projects/${project.id}`, {
        method: "DELETE", headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to delete project.");
      toast.success(`"${project.name}" deleted.`);
      setProjects((prev) => prev.filter((p) => p.id !== project.id));
      setHeaderStats((prev) => ({
        ...prev,
        total: Math.max(0, prev.total - 1),
        active:    project.status === "Active"    ? Math.max(0, prev.active - 1)    : prev.active,
        completed: project.status === "Completed" ? Math.max(0, prev.completed - 1) : prev.completed,
        onHold:    project.status === "On Hold"   ? Math.max(0, prev.onHold - 1)    : prev.onHold,
      }));
    } catch (err) {
      toast.error(err.message || "Cannot delete — project may be linked to active tasks.");
    }
  };

  const confirmArchive = async (project) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE_URL}/api/projects/${project.id}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ ...project, status: "Archived" }),
      });
      if (!res.ok) throw new Error("Failed to archive project.");
      toast.success(`"${project.name}" archived.`);
      setProjects((prev) => prev.map((p) => p.id === project.id ? { ...p, status: "Archived" } : p));
      setHeaderStats((prev) => ({
        ...prev,
        active:    project.status === "Active"    ? Math.max(0, prev.active - 1)    : prev.active,
        completed: project.status === "Completed" ? Math.max(0, prev.completed - 1) : prev.completed,
        onHold:    project.status === "On Hold"   ? Math.max(0, prev.onHold - 1)    : prev.onHold,
      }));
    } catch (err) {
      toast.error(err.message || "Failed to archive.");
    }
  };

  const downloadReport = async (format = "xlsx") => {
    try {
      setDownloading(true);
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE_URL}/api/reports/projects?format=${format}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`Failed to download ${format} report`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `Projects_Report.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Downloaded successfully");
    } catch (err) {
      toast.error(err.message || "Failed to download report");
    } finally {
      setDownloading(false);
    }
  };

  const modalConfig = modal.type === "delete"
    ? { title: "Delete Project", message: <>Are you sure you want to permanently delete <strong>"{modal.project?.name}"</strong>? This action <strong>cannot be undone</strong> and may affect linked tasks.</>, confirmLabel: "Delete Project", confirmVariant: "danger", icon: "🗑️" }
    : { title: "Archive Project", message: <>Archive <strong>"{modal.project?.name}"</strong>? The project will be moved to Archived status and hidden from active views.</>, confirmLabel: "Archive Project", confirmVariant: "warning", icon: "📦" };

  const PaginationBtn = ({ onClick, disabled, children }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: "5px 12px", borderRadius: T.radiusSm, border: `1.5px solid ${T.border}`,
        background: disabled ? T.surfaceAlt : T.surface, color: disabled ? T.textLight : T.textMuted,
        fontSize: 13, cursor: disabled ? "not-allowed" : "pointer", fontWeight: 500,
      }}
    >
      {children}
    </button>
  );

  return (
    <>
      <ConfirmModal
        isOpen={modal.isOpen}
        onClose={closeModal}
        onConfirm={handleModalConfirm}
        title={modalConfig.title}
        message={modalConfig.message}
        confirmLabel={modalConfig.confirmLabel}
        confirmVariant={modalConfig.confirmVariant}
        icon={modalConfig.icon}
      />

      <div className="fixed-top"><AdminNavbar /></div>

      <div style={{ paddingTop: 100, minHeight: "100vh", background: "#f8fafc", fontFamily: "'Inter', system-ui, sans-serif" }}>

        {/* ── Loading Spinner ── */}
        {loading && (
          <div style={{ position: "fixed", inset: 0, zIndex: 999, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "rgba(248,250,252,0.85)", backdropFilter: "blur(2px)" }}>
            <style>{`@keyframes proj-spin { to { transform: rotate(360deg); } }`}</style>
            <div style={{ width: 44, height: 44, border: `3px solid ${T.primaryLight}`, borderTopColor: T.primary, borderRadius: "50%", animation: "proj-spin 0.7s linear infinite" }} />
            <span style={{ marginTop: 14, fontSize: 13, color: T.textMuted, fontWeight: 500 }}>Loading projects…</span>
          </div>
        )}

        <div style={{ padding: "20px 24px" }}>

          {/* ── Header ── */}
          <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: "20px 24px", marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "center", boxShadow: T.shadow }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                <div style={{ width: 36, height: 36, borderRadius: 8, background: T.primaryLight, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <i className="bi bi-kanban-fill" style={{ color: T.primary, fontSize: 17 }} />
                </div>
                <h5 style={{ margin: 0, color: T.text, fontWeight: 700, fontSize: 18 }}>Projects</h5>
              </div>
              <div style={{ display: "flex", gap: 16, marginTop: 4, paddingLeft: 2 }}>
                <span style={{ color: T.textMuted, fontSize: 12 }}>
                  <span style={{ fontWeight: 700, fontSize: 15, marginRight: 4, color: T.text }}>{headerStats.total}</span>Total
                </span>
                <span style={{ color: T.textMuted, fontSize: 12 }}>
                  <span style={{ fontWeight: 700, fontSize: 15, marginRight: 4, color: T.success }}>{headerStats.active}</span>Active
                </span>
                <span style={{ color: T.textMuted, fontSize: 12 }}>
                  <span style={{ fontWeight: 700, fontSize: 15, marginRight: 4, color: "#0891b2" }}>{headerStats.completed}</span>Completed
                </span>
                <span style={{ color: T.textMuted, fontSize: 12 }}>
                  <span style={{ fontWeight: 700, fontSize: 15, marginRight: 4, color: T.warning }}>{headerStats.onHold}</span>On Hold
                </span>
              </div>
            </div>
            <button
              onClick={() => navigate("/addproject")}
              style={{ background: T.primary, color: "#fff", border: "none", borderRadius: T.radiusSm, padding: "8px 16px", fontWeight: 600, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, boxShadow: "0 2px 8px rgba(99,102,241,0.25)" }}
            >
              <i className="bi bi-plus-lg" /> New Project
            </button>
          </div>

          {/* ── Toolbar ── */}
          <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: "12px 16px", marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, boxShadow: T.shadow }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>

              {/* Search */}
              <div style={{ position: "relative" }}>
                <i className="bi bi-search" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: T.textLight, fontSize: 13 }} />
                <input
                  type="text"
                  placeholder="Search name, key, reference…"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setCurrentPage(0); }}
                  style={{ paddingLeft: 30, paddingRight: 10, paddingTop: 7, paddingBottom: 7, border: `1.5px solid ${search ? T.primary : T.border}`, borderRadius: T.radiusSm, fontSize: 13, outline: "none", width: 210, color: T.text, transition: "border-color 0.15s", background: T.surface }}
                />
              </div>

              {/* Status */}
              <select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(0); }}
                style={{ padding: "7px 10px", border: `1.5px solid ${statusFilter ? T.primary : T.border}`, borderRadius: T.radiusSm, fontSize: 13, color: statusFilter ? T.text : T.textMuted, outline: "none", cursor: "pointer", background: T.surface }}
              >
                <option value="">All Statuses</option>
                {STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>

              {/* Type */}
              <select
                value={typeFilter}
                onChange={(e) => { setTypeFilter(e.target.value); setCurrentPage(0); }}
                style={{ padding: "7px 10px", border: `1.5px solid ${typeFilter ? T.primary : T.border}`, borderRadius: T.radiusSm, fontSize: 13, color: typeFilter ? T.text : T.textMuted, outline: "none", cursor: "pointer", background: T.surface }}
              >
                <option value="">All Types</option>
                {PROJECT_TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>

              {/* Priority */}
              <select
                value={priorityFilter}
                onChange={(e) => { setPriorityFilter(e.target.value); setCurrentPage(0); }}
                style={{ padding: "7px 10px", border: `1.5px solid ${priorityFilter ? T.primary : T.border}`, borderRadius: T.radiusSm, fontSize: 13, color: priorityFilter ? T.text : T.textMuted, outline: "none", cursor: "pointer", background: T.surface }}
              >
                <option value="">All Priorities</option>
                {PRIORITIES.map((p) => <option key={p}>{p}</option>)}
              </select>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <button
                onClick={() => downloadReport("xlsx")}
                disabled={downloading}
                title="Download Excel report"
                style={{ padding: "7px 12px", border: `1.5px solid ${T.border}`, borderRadius: T.radiusSm, background: T.surface, color: T.textMuted, fontSize: 13, cursor: downloading ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 5, fontWeight: 500, opacity: downloading ? 0.6 : 1 }}
              >
                <i className="bi bi-file-earmark-excel" style={{ color: "#16a34a" }} />
                {downloading ? "Downloading…" : "Export"}
              </button>
              <div style={{ padding: "6px 12px", background: T.primaryLight, color: T.primary, borderRadius: T.radiusSm, fontSize: 12, fontWeight: 600 }}>
                {totalElements} result{totalElements !== 1 ? "s" : ""}
              </div>
            </div>
          </div>

          {/* ── Table ── */}
          <div style={{ background: T.surface, borderRadius: T.radius, border: `1px solid ${T.border}`, boxShadow: T.shadow, overflow: "hidden" }}>
            <div style={{ maxHeight: "65vh", overflowY: "auto", overflowX: "auto", scrollbarWidth: "thin", scrollbarColor: "#cbd5e1 transparent" }}>
              <style>{`
                .proj-table::-webkit-scrollbar { width: 6px; height: 6px; }
                .proj-table::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
                .proj-table::-webkit-scrollbar-track { background: transparent; }
              `}</style>
              <table className="proj-table" style={{ width: "max-content", minWidth: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
                <thead>
                  <tr>
                    <th style={{ position: "sticky", top: 0, left: 0, zIndex: 20, background: "#f1f5f9", padding: "10px 14px", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.6, color: T.textMuted, borderBottom: `2px solid ${T.border}`, whiteSpace: "nowrap" }}>
                      View | Edit
                    </th>
                    <Th label="Key"          sortKey="key"          sortConfig={sortConfig} onSort={handleSort} />
                    <Th label="Project"      sortKey="name"         sortConfig={sortConfig} onSort={handleSort} />
                    <Th label="Type"         sortKey="projectType"  sortConfig={sortConfig} onSort={handleSort} />
                    <Th label="Status"       sortKey="status"       sortConfig={sortConfig} onSort={handleSort} />
                    <Th label="Priority"     sortKey="priority"     sortConfig={sortConfig} onSort={handleSort} />
                    <Th label="Visibility"   sortKey={null}         sortConfig={sortConfig} onSort={handleSort} />
                    <Th label="Customer"     sortKey={null}         sortConfig={sortConfig} onSort={handleSort} />
                    <Th label="Start"        sortKey="startDate"    sortConfig={sortConfig} onSort={handleSort} />
                    <Th label="End"          sortKey="endDate"      sortConfig={sortConfig} onSort={handleSort} />
                    <Th label="Budget"       sortKey={null}         sortConfig={sortConfig} onSort={handleSort} />
                    <Th label="Created"      sortKey="createdAt"    sortConfig={sortConfig} onSort={handleSort} />
                    <Th label="Actions"      sortKey={null}         sortConfig={sortConfig} onSort={handleSort} />
                  </tr>
                </thead>
                <tbody>
                  {filteredProjects.length > 0 ? (
                    filteredProjects.map((p) => (
                      <tr
                        key={p.id}
                        onMouseEnter={() => setHoveredRow(p.id)}
                        onMouseLeave={() => setHoveredRow(null)}
                        style={{ background: hoveredRow === p.id ? "#f8faff" : T.surface, transition: "background 0.12s", borderBottom: `1px solid ${T.borderLight}` }}
                      >
                        {/* Sticky actions */}
                        <td style={{ position: "sticky", left: 0, background: hoveredRow === p.id ? "#f8faff" : T.surface, padding: "10px 14px", zIndex: 5, borderRight: `1px solid ${T.borderLight}`, whiteSpace: "nowrap" }}>
                          <Link to={`/viewproject/${p.id}`} style={{ fontSize: 12, fontWeight: 600, color: T.primary, textDecoration: "none", padding: "3px 8px", borderRadius: 4, background: T.primaryLight, marginRight: 6 }}>
                            View
                          </Link>
                          <Link to={`/editproject/${p.id}`} style={{ fontSize: 12, fontWeight: 600, color: "#64748b", textDecoration: "none", padding: "3px 8px", borderRadius: 4, background: "#f1f5f9" }}>
                            Edit
                          </Link>
                        </td>

                        {/* Key */}
                        <TdPlain>
                          <span style={{ background: T.primaryLight, color: T.primary, borderRadius: 4, padding: "2px 8px", fontFamily: "monospace", fontSize: 12, fontWeight: 600 }}>
                            {p.key || "—"}
                          </span>
                        </TdPlain>

                        {/* Project name with avatar */}
                        <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <ProjectAvatar project={p} />
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{p.name}</div>
                              {p.category && <div style={{ fontSize: 11, color: T.textMuted }}>{p.category}</div>}
                            </div>
                          </div>
                        </td>

                        {/* Type */}
                        <TdPlain>
                          {p.projectType ? (
                            <span style={{ background: "#ede9fe", color: "#7c3aed", padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
                              {p.projectType}
                            </span>
                          ) : val(null)}
                        </TdPlain>

                        <TdPlain><StatusBadge status={p.status} /></TdPlain>
                        <TdPlain><PriorityBadge priority={p.priority} /></TdPlain>

                        <TdPlain style={{ color: T.textMuted, fontSize: 12 }}>{val(p.visibility)}</TdPlain>
                        <TdPlain style={{ fontSize: 13 }}>{val(p.customerName)}</TdPlain>

                        <TdPlain style={{ fontFamily: "monospace", fontSize: 12 }}>{fmt(p.startDate)}</TdPlain>
                        <TdPlain style={{ fontFamily: "monospace", fontSize: 12 }}>{fmt(p.endDate)}</TdPlain>

                        <TdPlain style={{ fontFamily: "monospace", fontSize: 12 }}>
                          {p.estimatedBudget
                            ? <span style={{ color: T.success, fontWeight: 600 }}>{p.budgetCurrency || ""} {Number(p.estimatedBudget).toLocaleString("en-IN")}</span>
                            : <span style={{ color: T.textLight }}>—</span>}
                        </TdPlain>

                        <TdPlain style={{ fontFamily: "monospace", fontSize: 12 }}>{fmt(p.createdAt)}</TdPlain>

                        {/* Action buttons */}
                        <TdPlain>
                          <div style={{ display: "flex", gap: 6 }}>
                            {p.status !== "Archived" && (
                              <button
                                onClick={() => openArchiveModal(p)}
                                title="Archive"
                                style={{ padding: "4px 10px", borderRadius: T.radiusSm, border: `1.5px solid ${T.warning}`, background: T.warningBg, color: T.warning, fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center" }}
                              >
                                <i className="bi bi-archive" />
                              </button>
                            )}
                            <button
                              onClick={() => openDeleteModal(p)}
                              title="Delete"
                              style={{ padding: "4px 10px", borderRadius: T.radiusSm, border: `1.5px solid ${T.danger}`, background: T.dangerBg, color: T.danger, fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center" }}
                            >
                              <i className="bi bi-trash" />
                            </button>
                          </div>
                        </TdPlain>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={13} style={{ textAlign: "center", padding: "48px 20px", color: T.textLight }}>
                        <i className="bi bi-kanban" style={{ fontSize: 32, display: "block", marginBottom: 8 }} />
                        No projects found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Pagination ── */}
          {totalPages > 1 && (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 8, marginTop: 16 }}>
              <PaginationBtn onClick={() => setCurrentPage(0)} disabled={currentPage === 0}>«</PaginationBtn>
              <PaginationBtn onClick={() => setCurrentPage((p) => p - 1)} disabled={currentPage === 0}>‹ Prev</PaginationBtn>
              <span style={{ fontSize: 13, color: T.textMuted, padding: "5px 12px", background: T.primaryLight, borderRadius: T.radiusSm, fontWeight: 600, color: T.primary }}>
                Page {currentPage + 1} of {totalPages}
              </span>
              <PaginationBtn onClick={() => setCurrentPage((p) => p + 1)} disabled={currentPage >= totalPages - 1}>Next ›</PaginationBtn>
              <PaginationBtn onClick={() => setCurrentPage(totalPages - 1)} disabled={currentPage >= totalPages - 1}>»</PaginationBtn>
            </div>
          )}

          {error && (
            <div style={{ marginTop: 12, padding: "10px 14px", background: T.dangerBg, color: T.danger, borderRadius: T.radiusSm, fontSize: 13 }}>
              {error}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AllProjects;
